import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ExternalLink, Eye, EyeOff, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import FlagEmojiText from "../components/FlagEmojiText";
import { api } from "../lib/api";

const blankItem = {
  title: "",
  description: "",
  category: "Stream",
  start_time: "",
  end_time: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago",
  recurring_rule: "",
  enabled: true,
  public: true,
  active: false,
  color: "#BF5AF2",
  icon: "CalendarDays",
  link: "",
};

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";
const textareaClass = "w-full min-h-[110px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

function toInputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fromInputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function formFromItem(item) {
  if (!item) return blankItem;
  return {
    title: item.title || "",
    description: item.description || "",
    category: item.category || "Stream",
    start_time: toInputDate(item.start_time),
    end_time: toInputDate(item.end_time),
    timezone: item.timezone || blankItem.timezone,
    recurring_rule: item.recurring_rule || "",
    enabled: item.enabled !== false,
    public: item.public !== false,
    active: item.active === true,
    color: item.color || "#BF5AF2",
    icon: item.icon || "CalendarDays",
    link: item.link || "",
  };
}

function payloadFromForm(form) {
  return {
    title: form.title,
    description: form.description,
    category: form.category,
    start_time: fromInputDate(form.start_time),
    end_time: form.end_time ? fromInputDate(form.end_time) : null,
    timezone: form.timezone,
    recurring_rule: form.recurring_rule,
    enabled: Boolean(form.enabled),
    public: Boolean(form.public),
    active: Boolean(form.active),
    color: form.color,
    icon: form.icon,
    link: form.link,
  };
}

function formatDate(value) {
  if (!value) return "No time set";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  } catch (err) {
    return value;
  }
}

export default function AdminSchedule() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [createForm, setCreateForm] = useState(blankItem);
  const [editForm, setEditForm] = useState(blankItem);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || items[0] || null,
    [items, selectedId]
  );

  useEffect(() => {
    setEditForm(formFromItem(selected));
  }, [selected]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/schedule");
      const next = data.items || [];
      setItems(next);
      setSelectedId((current) => next.find((item) => item.id === current)?.id || next[0]?.id || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load schedule.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const replaceItem = (updated) => {
    setItems((current) => {
      const exists = current.some((item) => item.id === updated.id);
      const next = exists ? current.map((item) => (item.id === updated.id ? updated : item)) : [...current, updated];
      return next.sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")));
    });
    setSelectedId(updated.id);
  };

  const runAction = async (label, action) => {
    setBusy(label);
    setError("");
    setMessage("");
    try {
      const updated = await action();
      if (updated) replaceItem(updated);
    } catch (err) {
      setError(err.response?.data?.detail || "Schedule action failed.");
    } finally {
      setBusy("");
    }
  };

  const createItem = async (event) => {
    event.preventDefault();
    await runAction("create", async () => {
      const { data } = await api.post("/admin/schedule", payloadFromForm(createForm));
      setCreateForm(blankItem);
      setMessage("Schedule item created.");
      return data;
    });
  };

  const saveItem = async () => {
    if (!selected) return;
    await runAction("save", async () => {
      const { data } = await api.patch(`/admin/schedule/${selected.id}`, payloadFromForm(editForm));
      setMessage("Schedule item saved.");
      return data;
    });
  };

  const deleteItem = async () => {
    if (!selected) return;
    const ok = window.confirm(`Delete "${selected.title}" from the schedule?`);
    if (!ok) return;
    setBusy("delete");
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/schedule/${selected.id}`);
      setMessage("Schedule item deleted.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete schedule item.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Stream schedule</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[hsl(var(--primary))]" />
            Schedule
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Build a generic schedule for streams, community nights, segments, and future availability rules. Private or disabled items stay out of the public schedule.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading || !!busy}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {(error || message) && (
        <div className={`rounded border px-4 py-3 text-sm ${error ? "border-red-500/40 bg-red-500/10 text-red-100" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"}`}>
          {error || message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[22rem_1fr] gap-6">
        <aside className="space-y-4">
          <form onSubmit={createItem} className="surface p-5 space-y-4">
            <div>
              <div className="label-eyebrow">New item</div>
              <h2 className="text-lg font-semibold text-white mt-1">Add to schedule</h2>
            </div>
            <ScheduleFields form={createForm} onChange={setCreateForm} compact />
            <button disabled={!!busy} className="w-full inline-flex items-center justify-center gap-2 rounded bg-[hsl(var(--primary))] text-white py-2 text-sm font-medium disabled:opacity-50">
              <Plus className="w-4 h-4" /> Create item
            </button>
          </form>

          <div className="surface overflow-hidden">
            <div className="p-4 border-b border-[hsl(var(--border))]">
              <div className="label-eyebrow">Upcoming</div>
            </div>
            {loading ? (
              <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading schedule...</div>
            ) : items.length === 0 ? (
              <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No schedule items yet.</div>
            ) : (
              <div className="divide-y divide-[hsl(var(--border))]">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full text-left p-4 hover:bg-[hsl(var(--surface-2))] ${selected?.id === item.id ? "bg-[hsl(var(--surface-2))]" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate"><FlagEmojiText value={item.title} /></div>
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color || "#BF5AF2" }} />
                    </div>
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{formatDate(item.start_time)}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Pill>{item.category}</Pill>
                      {item.current && <Pill active>Current</Pill>}
                      {item.public ? <Pill>Public</Pill> : <Pill>Private</Pill>}
                      {!item.enabled && <Pill>Disabled</Pill>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <section className="surface p-5 space-y-5">
          {selected ? (
            <>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-eyebrow">Selected item</div>
                  <h2 className="text-xl font-semibold text-white mt-1"><FlagEmojiText value={selected.title} /></h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{formatDate(selected.start_time)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href="/schedule" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white">
                    <ExternalLink className="w-4 h-4" /> Public view
                  </a>
                  <button onClick={saveItem} disabled={!!busy} className="inline-flex items-center gap-2 rounded bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
                    <Save className="w-4 h-4" /> Save
                  </button>
                  <button onClick={deleteItem} disabled={!!busy} className="inline-flex items-center gap-2 rounded border border-red-500/40 text-red-100 px-4 py-2 text-sm hover:bg-red-500/10 disabled:opacity-50">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>

              <ScheduleFields form={editForm} onChange={setEditForm} />
            </>
          ) : (
            <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Select or create a schedule item to edit.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ScheduleFields({ form, onChange, compact = false }) {
  const set = (patch) => onChange({ ...form, ...patch });
  return (
    <div className="space-y-4">
      <Field label="Title">
        <input className={inputClass} value={form.title} onChange={(event) => set({ title: event.target.value })} required maxLength={140} />
      </Field>
      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <Field label="Start">
          <input type="datetime-local" className={inputClass} value={form.start_time} onChange={(event) => set({ start_time: event.target.value })} required />
        </Field>
        <Field label="End optional">
          <input type="datetime-local" className={inputClass} value={form.end_time} onChange={(event) => set({ end_time: event.target.value })} />
        </Field>
      </div>
      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-3 gap-4"}>
        <Field label="Category / type">
          <input className={inputClass} value={form.category} onChange={(event) => set({ category: event.target.value })} maxLength={80} />
        </Field>
        <Field label="Icon">
          <input className={inputClass} value={form.icon} onChange={(event) => set({ icon: event.target.value })} maxLength={80} />
        </Field>
        <Field label="Color">
          <input type="color" className={`${inputClass} p-1`} value={form.color} onChange={(event) => set({ color: event.target.value })} />
        </Field>
      </div>
      {!compact && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Timezone">
              <input className={inputClass} value={form.timezone} onChange={(event) => set({ timezone: event.target.value })} maxLength={80} />
            </Field>
            <Field label="Optional link">
              <input className={inputClass} value={form.link} onChange={(event) => set({ link: event.target.value })} maxLength={500} />
            </Field>
          </div>
          <Field label="Recurring rule optional">
            <input className={inputClass} value={form.recurring_rule} onChange={(event) => set({ recurring_rule: event.target.value })} placeholder="Future use, for example weekly repeats" maxLength={240} />
          </Field>
          <Field label="Description">
            <textarea className={textareaClass} value={form.description} onChange={(event) => set({ description: event.target.value })} maxLength={3000} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Toggle label="Enabled" checked={form.enabled} onChange={(enabled) => set({ enabled })} icon={form.enabled ? Eye : EyeOff} />
            <Toggle label="Public" checked={form.public} onChange={(value) => set({ public: value })} icon={form.public ? Eye : EyeOff} />
            <Toggle label="Mark current" checked={form.active} onChange={(active) => set({ active })} icon={CalendarDays} />
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label = "", checked, onChange, icon: Icon } = {}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm ${checked ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
    >
      <span>{label}</span>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function Pill({ children, active = false }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${active ? "border-emerald-500/40 text-emerald-100 bg-emerald-500/10" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
      {children}
    </span>
  );
}
